// "8:10 AM" from an ISO timestamp; otherwise the value as given.
//
// #688 — scheduledTime is an FVD STAMP, not a genuine UTC instant (ADODB on
// `fvdts` parses Access's naive venue wall-clock time as BOX-local, and
// foxbox reads it back with UTC getters — correct only because fvdts sits in
// America/Los_Angeles). #1811 split this file's old single VENUE_TIME_ZONE
// constant in two: FVD_STAMP_TIMEZONE (imported from common-fe) is the fixed
// zone every FVD-sourced timestamp renders through, no matter what the show's
// real venue is — see common-fe/src/utils/venueTime.js for the full mangling
// writeup, this is a deliberate duplicate VALUE, never the same constant as
// the venue default. NOT 'UTC' (that was this bug: it printed 4:44 PM where
// the day sheet shows 9:44 AM) and NOT browser-local (wrong for off-site
// viewers and in the report Lambda) — and (#1813) NOT the show's real
// venueTimezone either: that would be the exact WRONG half of the #1811
// split. Pointing an FVD stamp at the venue's real zone would shift every
// ride time by the fvdts/venue offset (3h at an Eastern show like WEC).
import {
  stampZoneOf,
  DEFAULT_VENUE_TIMEZONE,
  formatVenueTimeOrUnset,
  venueDayKey as venueLocalDayKey,
} from "common-fe/venueTime";

// #1814 AC3 -- `zone` is the row's optional `scheduledTimeZone` marker (pass it
// from the SAME row as `v`): foxbox composed the time in a configured venue
// zone, so the value is a genuine UTC instant read in THAT zone. Absent = the
// legacy FVD_STAMP_TIMEZONE read described above.
export function formatTime(v, zone) {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(v))) {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: stampZoneOf(zone),
      });
    }
  }
  return String(v);
}

// #691/#1813 — the sticky header's live "time of day" clock. Unlike
// formatTime above, its input (`date`) is a GENUINE INSTANT (`new Date()`),
// not an FVD stamp — there is no ingest mangling to cancel out here, so this
// is one of the two renderers in this file that follow the show's REAL
// venueTimezone rather than the fixed FVD stamp zone (venueTodayKey below is
// the other). #2097 — when the show has no usable venueTimezone this returns
// the explicit unset text (`unsetText`, default common-fe's English label)
// instead of the Pacific default it used to render: a clean, plausible,
// wrong clock is worse than none. An announcer standing in the ring must see
// the time the ring is on, not wherever his own browser happens to sit, and
// (#1813) not wherever FVD's ingest box happens to sit either. Distinct from
// formatClock.js's formatClockTime, which is deliberately BROWSER-local —
// that one is for client-side "this happened just now" strings like "last
// updated".
export function formatVenueClock(date, venueTimezone, unsetText) {
  return formatVenueTimeOrUnset(date, { timeZone: venueTimezone, seconds: true, unsetText });
}

// #892 — the venue-local CALENDAR DAY ("2026-07-17") an FVD STAMP falls on.
// `v` here is always an FVD-sourced timestamp (scheduledTime), never a
// genuine instant, so this stays on FVD_STAMP_TIMEZONE (#1811) — same #688
// reasoning as formatTime above, and it deliberately does NOT follow #1813's
// venueTimezone split. Rendering the stamp back through FVD_STAMP_TIMEZONE
// (Pacific) is the EXACT INVERSE of the ingest mangling (#688/#1811), so the
// key this produces already IS the true venue-local calendar day, in the
// venue's own terms — whatever the show's real timezone actually is. That is
// what makes it safe to compare against venueTodayKey below, which computes
// "today" through the show's REAL venue zone rather than this fixed one; see
// that function's comment for why the two sides use different zones and
// still land on the same calendar. en-CA is just a locale whose default date
// format happens to BE "YYYY-MM-DD" — a convenient, dependency-free way to
// pull a sortable/comparable key out of Intl instead of hand-rolling one.
// Deliberately NOT a naive string-slice of the ISO value (that's the OLDER,
// now-stale #634 report-defs convention from before foxbox emitted genuine
// UTC, #805) — a ride stamped late evening venue-local can be the NEXT day in
// UTC, and this must still group it under the venue day the day sheet shows.
function formatVenueDay(d, zone) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: stampZoneOf(zone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

// #1649 — this used to parse the incoming value with a bare
// `v instanceof Date ? v : new Date(v)`, with neither of the two input-shape
// guards report-defs' classDayKey() (#689) and scribe-fe's classDisplay.js
// (#1559) already carry:
//   1. require a date+time shape (`YYYY-MM-DDTHH:MM`) before parsing a
//      string at all. A date-ONLY string ("2026-08-04") parses as UTC
//      MIDNIGHT, so converting it straight to the venue zone reads back
//      "2026-08-03" — off by one, the day BEFORE the one the string names.
//      There is no way to recover the intended venue day from a date-only
//      value, so this returns null rather than guess wrong. That null is
//      not a silent drop on this surface: orderOfGo.js's groupRowsByDay()
//      already falls a null dayKey back to the caller's current venue day
//      (`unknownDayKey`) rather than a "no day" bucket of its own, so a
//      date-only scheduledTime still shows under today, just not keyed to
//      an invented/wrong day.
//   2. append a "Z" when a datetime string carries no zone marker. Per
//      ECMA-262 an unzoned datetime string ("2026-08-04T05:00:00.000")
//      parses as DEVICE-local, so the key would depend on the announcer
//      laptop's own timezone — scheduledTime always MEANS UTC (#805/#688),
//      zone marker or not.
// scheduledTime is genuine UTC — Date instances (the venueTodayKey path,
// `new Date()`) and already-zoned strings are unaffected by either guard.
//
// #1814 AC3 -- `zone` is the stamp's own `scheduledTimeZone` marker (absent =
// legacy stamp zone); the key is the calendar day in the zone it was minted in.
export function venueDayKey(v, zone) {
  if (!v) return null;
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? null : formatVenueDay(v, zone);
  }
  const raw = String(v);
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return null;
  const iso = /[Zz]|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : formatVenueDay(d, zone);
}

// "today" as the VENUE sees it right now — corrected in #1813 (was: the FVD
// stamp zone, which was backwards; see that card for the measured 00:00-03:00
// venue-local desync this replaced).
//
// `now` (default `new Date()`) is a GENUINE INSTANT, so per the #1813 split
// this belongs on the show's REAL venueTimezone, not the fixed
// FVD_STAMP_TIMEZONE — the same rule formatVenueClock above follows, and the
// mirror image of venueDayKey directly above, which stays on the stamp zone
// because ITS input is an FVD stamp, not an instant.
//
// The two sides still compare correctly despite using different zones:
// venueDayKey(scheduledTime) already recovers the TRUE venue-local calendar
// day (rendering an FVD stamp back through Pacific is the exact inverse of
// the ingest mangling — the digits that come out are the venue's own wall
// clock digits, whatever the venue's real zone is). So the only way to ask
// "is scheduledTime's venue day the same as today's venue day" is to compute
// "today" through that SAME real venue zone — using the fixed stamp zone
// here instead would desync the two by the fvdts/venue offset for part of
// the night (measured: an Eastern show at 2026-03-15 01:00 EDT is already
// 03-15 venue-local, but the stamp zone reads 03-14, silently hiding that
// day's rides from the today-only view for the 00:00-03:00 venue-local
// window). Falls back to DEFAULT_VENUE_TIMEZONE (Pacific) when the show has
// no venueTimezone set, matching formatVenueClock and leaving today's
// rendering unchanged for every show that has none. Note this deliberately
// does NOT call the local, guarded venueDayKey above (#1649) — that guard
// only matters for FVD-stamp strings, and venueTodayKey's input is always a
// genuine Date instance headed for the real venue zone, not the stamp zone.
export function venueTodayKey(now = new Date(), venueTimezone = DEFAULT_VENUE_TIMEZONE) {
  return venueLocalDayKey(now, venueTimezone || DEFAULT_VENUE_TIMEZONE);
}

// "THURSDAY" from a venueDayKey ("2026-07-17"). Deliberately derived from the
// KEY STRING, not by re-parsing the original timestamp through the venue
// timezone a second time: noon-padding a bare date string
// (`${key}T12:00:00`) sidesteps any zone/DST edge shifting the calendar date,
// same trick report-defs' class-day.js dayHeading() already uses for this
// exact reason.
export function dayKeyLabel(dayKey) {
  if (!dayKey) return "";
  const d = new Date(`${dayKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase();
}

export default formatTime;
